// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { Avatar, Box, Button, CardActions, List, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import ItemCountCard from './ItemCountCard';

// assets
import { OverrideIcon } from 'types';
import { useTranslation } from 'react-i18next';
import darkThemeIcon from 'assets/images/halog-icon-dark-theme.png';
import lightThemeIcon from 'assets/images/halog-icon-light-theme.png';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

// styles
const CardWrapper = styled(MainCard)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.light,
    overflow: 'hidden',
    position: 'relative',
    '&:after': {
        content: '""',
        position: 'absolute',
        width: 210,
        height: 210,
        background: `linear-gradient(210.04deg, ${theme.palette.primary[200]} -50.94%, rgba(144, 202, 249, 0) 83.49%)`,
        borderRadius: '50%',
        top: -30,
        right: -180
    },
    '&:before': {
        content: '""',
        position: 'absolute',
        width: 210,
        height: 210,
        background: `linear-gradient(140.9deg, ${theme.palette.primary[200]} -14.02%, rgba(144, 202, 249, 0) 77.58%)`,
        borderRadius: '50%',
        top: -160,
        right: -130
    }
}));

interface ItemCountCardProps {
    labelText: string;
    icon: OverrideIcon;
    redirectPath: string;
    countFunction: () => Promise<number>;
}

const ItemCountDarkCard = ({ labelText, icon, redirectPath, countFunction }: ItemCountCardProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const Icon = icon!;
    const [countElements, setCountElements] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setCountElements(await countFunction());
            setIsLoading(false);
        })();
    }, []);

    const redirect = () => {
        navigate(redirectPath, { replace: true });
    };

    return (
        <>
            {isLoading ? (
                <ItemCountCard />
            ) : (
                <CardWrapper border={false} content={false}>
                    <Box sx={{ p: 2 }}>
                        <List sx={{ py: 0 }}>
                            <ListItem alignItems="center" disableGutters sx={{ py: 0 }}>
                                <ListItemAvatar>
                                    <Avatar
                                        variant="rounded"
                                        sx={{
                                            ...theme.typography.commonAvatar,
                                            ...theme.typography.largeAvatar,
                                            backgroundColor:
                                                theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[100],
                                            color: '#fff'
                                        }}
                                    >
                                        {labelText === 'DEVICES' ? (
                                            <img
                                                src={theme.palette.mode === 'dark' ? darkThemeIcon : lightThemeIcon}
                                                style={{ width: '24px', height: '24px' }}
                                                alt=""
                                            />
                                        ) : (
                                            icon && (
                                                <Icon
                                                    size="30px"
                                                    sx={{ fontSize: '1.5rem', color: theme.palette.primary.main }}
                                                    color={theme.palette.primary.main}
                                                />
                                            )
                                        )}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    sx={{
                                        py: 0,
                                        mt: 0.45,
                                        mb: 0.45
                                    }}
                                    primary={
                                        <Typography variant="h4" sx={{ color: 'primary.light' }}>
                                            {countElements.toString()}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="subtitle2" sx={{ color: 'primary.light', mt: 0.25 }}>
                                            {t(labelText)}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        </List>
                    </Box>
                    <Box>
                        <CardActions sx={{ p: 1.25, pt: 0, justifyContent: 'center' }}>
                            <Button onClick={redirect} size="small" disableElevation sx={{ color: 'primary.light' }}>
                                {t('VIEW_ALL')}
                                <ChevronRightOutlinedIcon />
                            </Button>
                        </CardActions>
                    </Box>
                </CardWrapper>
            )}
        </>
    );
};

export default ItemCountDarkCard;
